import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getOddsFormatIs, ODDS_FORMAT, type OddsFormat } from '@cx-web/domain-types';
import classnames from 'classnames';

import styles from './OddsFormatDropdownLegacy.module.css';

type OddsFormatDropdownLegacyProps = {
  oddsFormat: OddsFormat;
  onOddsFormatChange: (format: OddsFormat) => void;
};

const MENU_ID = 'odds-format-menu';

const OddsFormatDropdownLegacy: React.FC<OddsFormatDropdownLegacyProps> = ({ oddsFormat, onOddsFormatChange }) => {
  const oddsFormatIs = getOddsFormatIs(oddsFormat);
  const { t } = useTranslation(['label']);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const label = t('RESPONSIVE_ODDS_FORMAT');

  const selectFormat = (format: OddsFormat, isSelected: boolean) => {
    if (!isSelected) onOddsFormatChange(format);
    setIsOpen(false);
  };

  const renderOption = (
    format: OddsFormat,
    isSelected: boolean,
    testId: string,
    optionLabel: string,
    example: string,
  ) => (
    <button
      aria-pressed={isSelected}
      className={classnames(styles.format, isSelected && styles.selected)}
      data-testid={testId}
      onClick={() => selectFormat(format, isSelected)}
      type="button"
    >
      {optionLabel}
      <span className={styles.example}>{example}</span>
    </button>
  );

  return (
    <div className={styles.formatsContainer} data-open={isOpen} data-testid="odds-format" ref={containerRef}>
      <button
        aria-controls={MENU_ID}
        aria-expanded={isOpen}
        aria-haspopup="true"
        className={styles.cta}
        data-testid="odds-format-trigger"
        onClick={() => setIsOpen(prev => !prev)}
        type="button"
      >
        {label}
        <span aria-hidden="true" className={styles.indicator}>
          &#x25BE;
        </span>
      </button>

      <div aria-label={label} className={styles.formats} id={MENU_ID} role="group">
        {renderOption(
          ODDS_FORMAT.AMERICAN,
          oddsFormatIs.American,
          'odds-format-option-american',
          t('RESPONSIVE_ODDS_FORMAT_AMERICAN'),
          '+125',
        )}
        {renderOption(
          ODDS_FORMAT.DECIMAL,
          oddsFormatIs.Decimal,
          'odds-format-option-decimal',
          t('RESPONSIVE_ODDS_FORMAT_DECIMAL'),
          '2.25',
        )}
        {renderOption(
          ODDS_FORMAT.FRACTIONAL,
          oddsFormatIs.Fractional,
          'odds-format-option-fractional',
          t('RESPONSIVE_ODDS_FORMAT_FRACTIONAL'),
          '5/4',
        )}
      </div>
    </div>
  );
};

export default OddsFormatDropdownLegacy;
